// CB:BEGIN homeowner-documents-modal
import React, { useState, useEffect, useRef } from 'react';
import { useApi } from '../../lib/useApi';
import { getApiUrl } from '../../lib/getApiUrl';
import { FaFile, FaUpload, FaDownload, FaTrash, FaTimes } from 'react-icons/fa';

interface Document {
  id: string;
  fileName: string;
  fileType: string;
  s3Key: string;
  uploadedAt: string;
  uploadedBy?: string;
  downloadUrl?: string;
}

interface HomeownerDocumentsModalProps {
  homeownerId: string;
  homeownerName?: string;
  isOpen: boolean;
  onClose: () => void;
}

const HomeownerDocumentsModal: React.FC<HomeownerDocumentsModalProps> = ({
  homeownerId,
  homeownerName,
  isOpen,
  onClose
}) => {
  const api = useApi();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && homeownerId) {
      fetchDocuments();
    }
  }, [isOpen, homeownerId]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.get<{ documents: Document[] }>(
        `/api/admin/homeowners/${homeownerId}/documents`
      );
      setDocuments(data.documents || []);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch documents');
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type (PDF or Word doc)
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    const allowedExtensions = ['.pdf', '.doc', '.docx'];
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      setError('Please upload a PDF or Word document (.pdf, .doc, or .docx)');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      // Upload file through backend proxy to avoid CORS issues
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fileName', file.name);
      formData.append('fileType', file.type || 'application/pdf');

      // api.post handles FormData correctly
      const uploadResponse = await api.post(
        `/api/admin/homeowners/${homeownerId}/documents/upload`,
        formData
      );

      if (!uploadResponse.success && !uploadResponse.document) {
        throw new Error('Failed to upload document');
      }

      // Refresh documents list
      await fetchDocuments();

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('Error uploading document:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (document: Document) => {
    try {
      const response = await api.get<{ downloadUrl: string; fileName: string }>(
        `/api/admin/homeowners/${homeownerId}/documents/${document.id}/download`
      );
      
      // Fetch the file and download with original filename
      const fileResponse = await fetch(response.downloadUrl);
      const blob = await fileResponse.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.fileName || document.fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading document:', err);
      setError(err instanceof Error ? err.message : 'Failed to download document');
    }
  };

  const handleDelete = async (document: Document) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${document.fileName}"? This action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      await api.delete(`/api/admin/homeowners/${homeownerId}/documents/${document.id}`);
      await fetchDocuments();
    } catch (err) {
      console.error('Error deleting document:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete document');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '800px',
          width: '90%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h2 style={{ fontSize: '24px', fontWeight: '700', margin: 0, marginBottom: '4px' }}>
              Documents
            </h2>
            {homeownerName && (
              <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                {homeownerName}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <FaTimes size={20} color="#6b7280" />
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div
            style={{
              backgroundColor: '#FEF2F2',
              border: '1px solid #FECACA',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '16px',
              color: '#991B1B'
            }}
          >
            {error}
          </div>
        )}

        {/* Upload Section */}
        <div
          style={{
            border: '2px dashed #d1d5db',
            borderRadius: '8px',
            padding: '24px',
            textAlign: 'center',
            marginBottom: '24px',
            backgroundColor: '#f9fafb'
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <FaUpload size={32} color="#6b7280" style={{ marginBottom: '12px' }} />
          <p style={{ margin: '0 0 12px 0', color: '#374151', fontWeight: '500' }}>
            Upload Contract or Document
          </p>
          <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#6b7280' }}>
            PDF or Word documents only (max 10MB)
          </p>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="btn btn-primary"
            style={{
              fontSize: '14px',
              padding: '10px 20px',
              opacity: uploading ? 0.6 : 1,
              cursor: uploading ? 'not-allowed' : 'pointer'
            }}
          >
            {uploading ? 'Uploading...' : 'Choose File'}
          </button>
        </div>

        {/* Documents List */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p style={{ marginTop: '16px', color: '#6b7280' }}>Loading documents...</p>
          </div>
        ) : documents.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            <FaFile size={48} style={{ marginBottom: '16px', opacity: 0.3 }} />
            <p style={{ fontSize: '18px', fontWeight: '500', margin: 0 }}>No Documents Found</p>
            <p style={{ fontSize: '14px', margin: '8px 0 0 0', opacity: 0.8 }}>
              Upload a signed CoreBids contract or other document to get started
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {documents.map((doc) => (
              <div
                key={doc.id}
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: '#f9fafb'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                  <FaFile size={24} color="#3b82f6" />
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontWeight: '500', color: '#111827' }}>
                      {doc.fileName}
                    </p>
                    <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#6b7280' }}>
                      Uploaded {formatDate(doc.uploadedAt)}
                      {doc.uploadedBy && ` by ${doc.uploadedBy}`}
                    </p>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => handleDownload(doc)}
                    className="btn btn-secondary"
                    style={{ fontSize: '12px', padding: '6px 12px' }}
                    title="Download"
                  >
                    <FaDownload size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(doc)}
                    className="btn"
                    style={{
                      fontSize: '12px',
                      padding: '6px 12px',
                      backgroundColor: '#dc2626',
                      color: 'white',
                      border: 'none'
                    }}
                    title="Delete"
                  >
                    <FaTrash size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HomeownerDocumentsModal;
// CB:END homeowner-documents-modal

